import { useState, useEffect } from "react";

interface AnimatedLogoProps {
  className?: string;
  isHovered?: boolean;
}

const AnimatedLogo = ({ className = "w-5 h-5", isHovered: externalHovered }: AnimatedLogoProps) => {
  const [internalHovered, setInternalHovered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const isHovered = externalHovered ?? internalHovered;

  // Track if we should show the animation (either hovering or animation still in progress)
  const showAnimation = isHovered || isAnimating;

  // Start animation when hover begins
  useEffect(() => {
    if (isHovered) {
      setIsAnimating(true);
    }
  }, [isHovered]);

  const handleAnimationEnd = (e: React.AnimationEvent) => {
    // Only handle the cap-toss animation ending
    if (e.animationName === "cap-toss") {
      setIsAnimating(false);
    }
  };

  return (
    <div
      className={`relative overflow-visible ${className}`}
      onMouseEnter={() => setInternalHovered(true)}
      onMouseLeave={() => setInternalHovered(false)}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full overflow-visible"
        style={{ overflow: "visible" }}
      >
        {/* Graduation Cap */}
        <g
          className={`origin-center transition-transform duration-500 ease-out ${
            showAnimation ? "animate-cap-toss" : ""
          }`}
          onAnimationEnd={handleAnimationEnd}
          style={{
            transformOrigin: "12px 12px",
          }}
        >
          {/* Cap top (mortarboard) */}
          <path
            d="M12 3L2 8L12 13L22 8L12 3Z"
            fill="currentColor"
            className="text-gray-700"
          />
          {/* Cap bottom/band */}
          <path
            d="M5 10.5V15C5 15 8 18 12 18C16 18 19 15 19 15V10.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-600"
            fill="none"
          />
          {/* Tassel string */}
          <path
            d="M19 8V12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="text-gray-600"
          />
          {/* Tassel */}
          <circle
            cx="19"
            cy="13.5"
            r="1"
            fill="currentColor"
            className={`text-gray-600 ${showAnimation ? "animate-tassel-swing" : ""}`}
          />
          <path
            d="M19 14.5V17"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            className={`text-gray-600 ${showAnimation ? "animate-tassel-swing" : ""}`}
            style={{ transformOrigin: "19px 13.5px" }}
          />
        </g>

        {/* Sparkles that appear on hover */}
        {isHovered && (
          <>
            <circle
              cx="6"
              cy="5"
              r="1"
              fill="currentColor"
              className="text-yellow-500 animate-sparkle-1"
            />
            <circle
              cx="18"
              cy="4"
              r="0.8"
              fill="currentColor"
              className="text-yellow-500 animate-sparkle-2"
            />
            <circle
              cx="4"
              cy="12"
              r="0.6"
              fill="currentColor"
              className="text-yellow-500 animate-sparkle-3"
            />
            <circle
              cx="20"
              cy="11"
              r="0.7"
              fill="currentColor"
              className="text-yellow-500 animate-sparkle-4"
            />
          </>
        )}
      </svg>

      <style>{`
        @keyframes cap-toss {
          0% {
            transform: translateY(0) rotate(0deg);
          }
          30% {
            transform: translateY(-8px) rotate(-10deg);
          }
          50% {
            transform: translateY(-12px) rotate(5deg);
          }
          70% {
            transform: translateY(-6px) rotate(-3deg);
          }
          100% {
            transform: translateY(0) rotate(0deg);
          }
        }

        @keyframes tassel-swing {
          0%, 100% {
            transform: rotate(0deg);
          }
          25% {
            transform: rotate(15deg);
          }
          75% {
            transform: rotate(-15deg);
          }
        }

        @keyframes sparkle-1 {
          0% {
            opacity: 0;
            transform: scale(0) translateY(0);
          }
          50% {
            opacity: 1;
            transform: scale(1.2) translateY(-3px);
          }
          100% {
            opacity: 0;
            transform: scale(0) translateY(-6px);
          }
        }

        @keyframes sparkle-2 {
          0% {
            opacity: 0;
            transform: scale(0) translateY(0);
          }
          40% {
            opacity: 1;
            transform: scale(1) translateY(-4px);
          }
          100% {
            opacity: 0;
            transform: scale(0) translateY(-8px);
          }
        }

        @keyframes sparkle-3 {
          0% {
            opacity: 0;
            transform: scale(0);
          }
          60% {
            opacity: 1;
            transform: scale(1.1);
          }
          100% {
            opacity: 0;
            transform: scale(0);
          }
        }

        @keyframes sparkle-4 {
          0% {
            opacity: 0;
            transform: scale(0) translateY(0);
          }
          55% {
            opacity: 1;
            transform: scale(1) translateY(-2px);
          }
          100% {
            opacity: 0;
            transform: scale(0) translateY(-5px);
          }
        }

        .animate-cap-toss {
          animation: cap-toss 0.6s ease-out forwards;
        }

        .animate-tassel-swing {
          animation: tassel-swing 0.5s ease-in-out;
        }

        .animate-sparkle-1 {
          animation: sparkle-1 0.5s ease-out forwards;
        }

        .animate-sparkle-2 {
          animation: sparkle-2 0.6s ease-out forwards;
          animation-delay: 0.1s;
        }

        .animate-sparkle-3 {
          animation: sparkle-3 0.5s ease-out forwards;
          animation-delay: 0.15s;
        }

        .animate-sparkle-4 {
          animation: sparkle-4 0.55s ease-out forwards;
          animation-delay: 0.05s;
        }
      `}</style>
    </div>
  );
};

export default AnimatedLogo;
